<script setup>
import { ref, provide, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';
// custom: T8a PR3a — barra de busca local + filtro
import CwListSearch from 'dashboard/components-next/cw_custom/ConversationListSearch/CwListSearch.vue';
import { matchesQuery } from 'dashboard/composables/cw_custom/conversationListSearch';
// custom: T8a PR3b — camada server-side da busca híbrida
import { useConversationSearch } from 'dashboard/composables/cw_custom/useConversationSearch';
// custom: separa comunicação interna × externa (equipe interna) — mapa rota → escopo
import {
  internalScopeForRoute,
  triagemScopeForRoute,
} from 'dashboard/composables/cw_custom/internalScope';

// (O ChatListHeader saiu em 2026-08-08 — o título da coluna virou a migalha da barra do
// topo. O componente segue no repositório, sem uso, porque é arquivo do upstream: apagá-lo
// criaria conflito de merge sem ganho nenhum.)
import ConversationList from './ConversationList.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
// custom: T8a — ConversationFilter, SaveCustomView, DeleteCustomViews, TeleportWithDirection
// removidos (dependiam dos teleport targets extintos do ChatListHeader)
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
// custom: T8a — useFilter, languages, countries, generateValuesForEditCustomViews removidos
// (usados apenas pelas funções do filtro avançado que foram retiradas)
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
// custom: T8a — useCamelCase removido (era para modal de filtros)
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
// custom: T8a — advancedFilterOptions removido (era para advancedFilterTypes, agora morto)
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import {
  ASSIGNEE_TYPE_TAB_PERMISSIONS,
  // custom: T7 — permissões das abas de triagem
  CONVERSATION_TAB_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
// custom: T7 PR3 — getter de favoritas (módulo namespaced cwCustomConversationFavorites)
// useMapGetter não suporta namespace com '/' — acessamos via store.getters diretamente.
// Padrão: computed(() => store.getters['cwCustomConversationFavorites/getFavoriteIds'])

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

// `pageTitle` sobe para o pai (ConversationView) desde 2026-08-08: o título saiu da coluna e
// virou a MIGALHA da barra do topo. Sobe em vez de ser recalculado lá porque a conta tem
// ramos que já custaram bug uma vez (Triagem reusa esta tela e mostrava "Conversas"); duas
// cópias da mesma regra divergem no primeiro ajuste.
const emit = defineEmits(['conversationLoad', 'update:pageTitle']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);

// custom: T7 — assigneeType forçado em ALL: as abas T7 são triagem global, não por assignee.
// Sem isso, o backend aplica assigned_to(current_user) e "Tudo" volta apenas conversas
// atribuídas à Malu, escondendo leads não-atribuídos e conversas de outros agentes.
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
// custom: T7 — status fixo em ALL: as 3 abas de triagem operam sobre todas as conversas
// independente do status (resolve a dívida da T8a que travou em OPEN).
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
// custom: T7 — aba de triagem da operadora (Tudo / Não lidas / Favoritas)
const activeConversationTab = ref(wootConstants.CONVERSATION_TAB.ALL);
// custom: T8a — showAdvancedFilters, foldersQuery, showAddFoldersModal,
// showDeleteFoldersModal, appliedFilter removidos (dependiam dos 3 controles)
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
// custom: T8a — advancedFilterTypes removido (era para setParamsForEditFolderModal)

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
// custom: T7 — getMineChats/getUnAssignedChats mantidos como getters core (não deletar — §3.1)
// para viabilizar futura aba "Não atribuídas" (Q6) e merge de upstream sem conflito.
// eslint-disable-next-line no-unused-vars
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
// eslint-disable-next-line no-unused-vars
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
// custom: T8a — agentList, teamsList, inboxesList, campaigns, labels removidos
// (eram para setParamsForEditFolderModal, agora morto)
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');
// custom: T7 PR3 — Set de ids das conversas favoritadas pelo agente atual.
// Acesso direto via store.getters (namespace com '/' não é suportado por useMapGetter).
const favoriteIds = computed(
  () => store.getters['cwCustomConversationFavorites/getFavoriteIds']
);

const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
} = useBulkActions();

// custom: T8a — useFilter(...) e suas destruturas removidas (filtro avançado retirado)

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

// custom: T8a — activeFolderName removido (era para ConversationFilter, agora morto)

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// custom: T8a — currentUserDetails removido (era para initializeExistingFilterToModal)

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

// custom: T8a — conversationCustomAttributes removido (era para setParamsForEditFolderModal)

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

// custom: separa comunicação interna × externa por rota (ver CwCustom::ConversationFinderDecorator).
// Fonte única do mapeamento em composables/cw_custom/internalScope.js (compartilhada com
// ConversationItem.vue para manter lista e navegação em sincronia).
const internalScope = computed(() => internalScopeForRoute(route.name));
// custom (2026-08-07): a aba Triagem. 'only' na aba dela, 'exclude' na de Conversas — sem a
// segunda metade a mesma fila apareceria nos dois lugares.
const triagemScope = computed(() => triagemScopeForRoute(route.name));

const conversationFilters = computed(() => {
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    // custom: escopo interno/externo derivado da rota ativa
    internalScope: internalScope.value,
    // custom: escopo de triagem derivado da rota ativa
    triagemScope: triagemScope.value,
  };
});

// custom: T7 — itens das abas de triagem (Tudo / Não lidas / Favoritas)
// Definido após conversationFilters para poder usar o getter allChatList com os filtros corretos.
// "all" → allCount do servidor (contado com status=all e assignee=all).
// "unread" → filtro client-side sobre a lista já carregada (§5.2, Q4).
// "favorite" → favoriteIds.size (verdade global do agente, §5.3)
const conversationTabItems = computed(() => {
  const unreadCount = allChatList
    .value(conversationFilters.value)
    .filter(c => (c.unread_count || 0) > 0).length;

  // custom: T7 PR3 — contador de favoritas vem do Set carregado no mount (independe da paginação)
  const favoriteCount = favoriteIds.value.size;

  return filterItemsByPermission(
    CONVERSATION_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => {
    if (key === wootConstants.CONVERSATION_TAB.UNREAD) {
      return {
        key,
        name: t(`CHAT_LIST.CONVERSATION_TAB.${key}`),
        count: unreadCount,
      };
    }
    if (key === wootConstants.CONVERSATION_TAB.FAVORITE) {
      return {
        key,
        name: t(`CHAT_LIST.CONVERSATION_TAB.${key}`),
        count: favoriteCount,
      };
    }
    return {
      key,
      name: t(`CHAT_LIST.CONVERSATION_TAB.${key}`),
      count: countKey ? conversationStats.value[countKey] || 0 : 0,
    };
  });
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  // custom (2026-08-07): a aba Triagem reusa esta MESMA tela (ConversationView), então sem
  // este ramo o cabeçalho dela dizia "Conversas" — a lista certa com o nome da outra aba.
  // Vem primeiro porque é o contexto mais específico: em /triagem não há inbox, time nem
  // rótulo que possam dar um título melhor.
  if (triagemScope.value === 'only') {
    return t('SIDEBAR.TRIAGEM');
  }
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

// `immediate`: a barra do topo precisa do título já na primeira pintura, senão a migalha
// nasce vazia e preenche um quadro depois — piscada visível justamente no elemento que diz
// onde a pessoa está.
watch(pageTitle, titulo => emit('update:pageTitle', titulo), {
  immediate: true,
});

// custom: T7 — filterByAssigneeTab preservado para quando participating precisar de
// seletor de assignee dedicado (Q5/Q6). Não deletar — baixo custo, alto valor no merge.
// eslint-disable-next-line no-unused-vars
function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(c => !c.meta?.assignee);
  }
  return [...conversations];
}

function sortByUnreadStatus(conversations) {
  return [...conversations].sort((a, b) => {
    const unreadCountDiff = (b.unread_count || 0) - (a.unread_count || 0);
    if (unreadCountDiff !== 0) return unreadCountDiff;

    return (b.last_activity_at || 0) - (a.last_activity_at || 0);
  });
}

// custom: T7 — filtro de exibição por aba de triagem (não altera o fetch — status=all é fixo)
function filterByConversationTab(conversations) {
  if (activeConversationTab.value === wootConstants.CONVERSATION_TAB.UNREAD) {
    return conversations.filter(c => (c.unread_count || 0) > 0);
  }
  if (activeConversationTab.value === wootConstants.CONVERSATION_TAB.FAVORITE) {
    // custom: T7 PR3 — filtra pelas ids carregadas do servidor no mount
    const ids = favoriteIds.value;
    return conversations.filter(c => ids.has(c.id));
  }
  return [...conversations];
}

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      // custom: T7 — participating também usa filterByConversationTab: as abas valem lá
      // (Tudo=todas as participating, Não lidas=participating não lidas, Favoritas=placeholder).
      // filterByAssigneeTab removido deste path — activeAssigneeTab=ALL não filtraria nada
      // de útil e as abas ficariam mortas (regressão apontada pelo adversarial review).
      localConversationList = filterByConversationTab(
        participatingChatsList.value(filters)
      );
    } else {
      // custom: T7 — todas as abas de triagem usam allChatList (status=all fixo)
      localConversationList = filterByConversationTab(
        allChatList.value(filters)
      );
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  if (
    !hasAppliedFiltersOrActiveFolders.value &&
    activeSortBy.value === wootConstants.SORT_BY_TYPE.UNREAD
  ) {
    localConversationList = sortByUnreadStatus(localConversationList);
  }

  return localConversationList;
});

// custom: T8a — filtro local estilo WhatsApp sobre a lista carregada
const searchQuery = ref('');

// custom: T8a PR3b — busca server-side + estados de carregamento e erro.
// O filtro local continua disponível quando o histórico falha, mas a interface
// deixa claro que o resultado pode estar incompleto e permite tentar novamente.
const { serverResults, isSearching, searchError, retrySearch } =
  useConversationSearch(searchQuery);

// custom: T8a PR3b — seed da store de contatos com os senders dos resultados
// do servidor. ConversationItem resolve o contato via
// store.getters['contacts/getContact'](senderId); se o contato não estiver na
// store (conversa de histórico que nunca apareceu na lista), o card renderiza
// sem nome. Ao receber serverResults, populamos a store para que o getter ache.
// Mesmo padrão de conversations/actions.js:40 (getConversation).
watch(serverResults, results => {
  results.forEach(conv => {
    const sender = conv?.meta?.sender;
    if (sender?.id) {
      store.commit('contacts/SET_CONTACT_ITEM', sender);
    }
  });
});

const filteredConversationList = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return conversationList.value;

  // Resultado local (instantâneo)
  const local = conversationList.value.filter(c => matchesQuery(c, q));

  // custom: T8a PR3b — mescla com resultado server-side deduplicando por id
  // Local aparece primeiro (já está carregado); server completa o histórico.
  if (!serverResults.value.length) return local;

  const localIds = new Set(local.map(c => c.id));
  const serverOnly = serverResults.value.filter(c => !localIds.has(c.id));
  return [...local, ...serverOnly];
});

const showEndOfListMessage = computed(() => {
  return !!(
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { order_by: orderBy } = filterBy;
  // custom: T7 — status e assigneeType NÃO são lidos dos UISettings: as abas T7 forçam
  // status=all e assigneeType=all para fetch global. Ler os valores salvos voltaria a
  // travar em OPEN (T8a) ou em assigned-to-me (só conversas da Malu).
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  // custom: T8a — showAdvancedFilters removido; linha de reset omitida
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

// custom: T8a — onApplyFilter, closeAdvanceFiltersModal, onUpdateSavedFilter,
// onClickOpenAddFoldersModal, onCloseAddFoldersModal, onClickOpenDeleteFoldersModal,
// onCloseDeleteFoldersModal, setParamsForEditFolderModal, initializeExistingFilterToModal,
// initializeFolderToFilterModal, initalizeAppliedFiltersToModal, onToggleAdvanceFiltersModal
// removidos — todos dependiam dos 3 controles e do ConversationFilter/SaveCustomView
// que foram retirados do template (§0 ADENDO). Acesso a filtros será redesenhado na T7.

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  // custom: T8a — appliedFilter.value = [] removido (appliedFilter não existe mais)
  // custom: T8a fix — zera busca ao trocar contexto (aba/pasta/inbox/team)
  searchQuery.value = '';
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// custom: T7 — updateAssigneeTab removido: as abas de triagem substituem a UI de assignee.
// activeAssigneeTab permanece (usado em filterByAssigneeTab para o path participating).
// Reintroduzir updateAssigneeTab se participating precisar de seletor de aba próprio.

// custom: T7 — troca de aba de triagem (Tudo / Não lidas / Favoritas)
// O fetch não é redisparado: todas as abas consomem a mesma lista status=all já carregada.
// Apenas reseta bulk para não manter seleção cruzada entre abas.
function updateConversationTab(selectedTab) {
  if (activeConversationTab.value !== selectedTab) {
    resetBulkActions();
    searchQuery.value = '';
    emitter.emit('clearSearchInput');
    activeConversationTab.value = selectedTab;
  }
}

// custom: T8a — onBasicFilterChange, openLastSavedItemInFolder,
// openLastItemAfterDeleteInFolder removidos (eram para os 3 controles e modais extintos)

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  // custom (equipe interna): marcar-não-lida/deletar dentro da inbox interna deve voltar
  // para /equipe, não para /dashboard (home = clientes). internalScope é derivado da rota
  // atual via internalScopeForRoute (fonte única — mesmo valor que alimenta o ChatList).
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
      internalScope: internalScope.value,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  // custom: T7 — status forçado em ALL antes de setar; setFiltersFromUISettings não o sobrescreve mais.
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
  // custom: T7 PR3 — carrega ids de favoritas do agente atual ao montar a lista
  store.dispatch('cwCustomConversationFavorites/load', currentAccountId.value);
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);
// custom: T7 PR3 — gate que habilita a ★ em ConversationCard; false fora do ChatList evita
// botão fantasma no painel de Contato (ContactConversations.vue) onde o clique não faria nada.
provide('canFavorite', true);
// custom: T7 PR3 — toggle de favorita injetado via provide/inject (padrão de selectConversation)
provide('toggleFavorite', conversationId =>
  store.dispatch('cwCustomConversationFavorites/toggle', {
    accountId: currentAccountId.value,
    conversationId,
  })
);
// custom: T7 PR3 — getter de favoritas injetado para ConversationCard verificar estado da ★
provide('isFavorited', conversationId => favoriteIds.value.has(conversationId));

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);
// custom: trocar entre "Conversas" (exclude) e "Caixa de Entrada" (only) reusa o mesmo
// ConversationView (inboxId: 0) — os props não mudam, então só o internalScope sinaliza a
// troca de aba. Sem este watch a lista não recarregaria ao alternar interna ↔ externa.
watch(internalScope, () => resetAndFetchData());
// custom (2026-08-07): mesma razão do watch acima — Triagem e Conversas reusam a MESMA
// instância de ConversationView (inboxId: 0), então os props não mudam ao trocar de aba e
// só o escopo sinaliza que a lista precisa ser refeita.
watch(triagemScope, () => resetAndFetchData());

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1 relative"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <!-- ⚠️ O TÍTULO DA COLUNA SAIU em 2026-08-08, quando a tela ganhou a barra do topo
         (TelaHeader, em ConversationView). Com as duas peças, "Conversas" aparecia duas
         vezes empilhado — uma na barra e outra 20px abaixo. Palavras dele: "não precisa dar
         outro nome pra aba; tira a de cima da barra de pesquisa e sobe a barra de pesquisa".
         A busca subiu para o lugar dele, e é isso que alinha o topo desta coluna com o topo
         do painel de conversa ao lado.
         (O `ChatListHeader` também carregava o badge de contagem, que só aparecia com filtro
         aplicado. Ele foi junto: contador sem título ao lado é um número órfão. Se a
         contagem fizer falta, o lugar dela é junto das abas Tudo/Não lidas/Favoritas, que
         já mostram números.) -->
    <!-- custom: T8a — barra de busca local; @add-folders/@delete-folders/@filters-modal/
         @reset-filters/@basic-filter-change removidos junto com os 3 controles do header -->
    <CwListSearch v-model="searchQuery" />
    <!-- custom: T8a PR3b — estados explícitos da busca server-side. -->
    <p
      v-if="isSearching"
      role="status"
      aria-live="polite"
      class="px-4 py-1 text-xs text-n-slate-10"
    >
      {{ $t('CHAT_LIST.SEARCH.LOADING') }}
    </p>
    <div
      v-else-if="searchError"
      role="alert"
      class="mx-3 mb-2 flex items-center gap-2 rounded-lg bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
    >
      <span class="i-lucide-circle-alert size-4 flex-shrink-0" />
      <span class="min-w-0 flex-1">
        {{ $t('CHAT_LIST.SEARCH.ERROR') }}
      </span>
      <Button
        :label="$t('CHAT_LIST.SEARCH.RETRY')"
        variant="ghost"
        color="ruby"
        size="xs"
        @click="retrySearch"
      />
    </div>

    <!-- custom: T7 — abas de triagem substituem abas de assignee na home/inbox padrão.
         Participating continua usando assignee via filterByAssigneeTab (Q5). -->
    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders"
      :items="conversationTabItems"
      :active-tab="activeConversationTab"
      is-compact
      @chat-tab-change="updateConversationTab"
    />

    <!-- custom: T8a — estado vazio diferenciado: busca ativa vs lista vazia real.
         PR3b: não exibe "vazio" enquanto isSearching (server ainda pode retornar) -->
    <p
      v-if="
        !chatListLoading &&
        !isSearching &&
        !searchError &&
        !filteredConversationList.length
      "
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{
        searchQuery.trim()
          ? $t('CHAT_LIST.SEARCH.EMPTY')
          : $t('CHAT_LIST.LIST.404')
      }}
    </p>
    <ConversationBulkActions
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      :class="isOnExpandedLayout && 'sm:!w-[24rem] !w-full'"
      @select-all-conversations="toggleSelectAll"
    />
    <!-- custom: T8a — lista filtrada em vez da lista bruta (filter só de exibição) -->
    <ConversationList
      :conversation-list="filteredConversationList"
      :is-loading="chatListLoading"
      :show-end-of-list-message="showEndOfListMessage"
      :label="label"
      :team-id="teamId"
      :folders-id="foldersId"
      :conversation-type="conversationType"
      :show-assignee="showAssigneeInConversationCard"
      :is-on-expanded-layout="isOnExpandedLayout"
      @load-more="loadMoreConversations"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <!-- custom: T8a — TeleportWithDirection para #conversationFilterTeleportTarget removido
         junto com o filtro avançado (target não existe mais no ChatListHeader). -->
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
